import React, { FC, useEffect, useMemo, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
    CategoryScale,
    Chart as ChartJS,
    ChartData,
    ChartOptions,
    Filler,
    LinearScale,
    LineElement,
    Plugin,
    PointElement,
    ScriptableContext,
    Tooltip,
    TooltipItem
} from 'chart.js';

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Filler);

// --- ХЕЛПЕРЫ ---

const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 39, g: 125, b: 255 };
};

const formatCompact = (value: number) => {
    if (value === 0) return '0';
    if (Math.abs(value) >= 1000000) {
        return (value / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (Math.abs(value) >= 1000) {
        return (value / 1000).toFixed(0) + 'k';
    }
    return value.toString();
};

const shadowLine: Plugin<'line'> = {
    id: 'shadowLine',
    beforeDatasetDraw(chart) {
        const { ctx } = chart;
        ctx.save();
        ctx.shadowBlur = 6;
        ctx.shadowOffsetY = 2;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
    },
    afterDatasetDraw(chart) {
        chart.ctx.restore();
    },
};

// --- КОМПОНЕНТ ---

interface MiniChartProps {
    data: number[];
    labels: string[];
    tooltipLabels?: string[];
    colorHex?: string;
    renderTooltip?: (dataIndex: number, value: number) => string | string[];
}

const MiniChart: FC<MiniChartProps> = ({
                                           data,
                                           labels,
                                           tooltipLabels,
                                           colorHex = '#277DFF',
                                           renderTooltip
                                       }) => {
    const chartRef = useRef<ChartJS<'line'> | undefined>(undefined);

    useEffect(() => {
        const forceResize = () => {
            const chart = chartRef.current;
            if (!chart) return;

            requestAnimationFrame(() => {
                chart.resize();
                chart.update('none');
            });
        };

        forceResize();
        window.addEventListener('resize', forceResize);
        window.addEventListener('orientationchange', forceResize);

        return () => {
            window.removeEventListener('resize', forceResize);
            window.removeEventListener('orientationchange', forceResize);
        };
    }, []);

    const chartOptions: ChartOptions<'line'> = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 0,
        onResize: (chart) => {
            requestAnimationFrame(() => {
                chart.update('none');
            });
        },
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: true,
                backgroundColor: '#141517',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#333',
                borderWidth: 1,
                // Увеличил padding, чтобы крупный текст не прилипал к краям
                padding: 14,
                cornerRadius: 8,
                displayColors: false,
                intersect: false,
                // --- НАСТРОЙКИ ШРИФТА ТУЛТИПА ---
                titleFont: {
                    size: 16, // Увеличил размер заголовка (дата)
                    weight: 'normal',
                    family: 'Inter, sans-serif'
                },
                bodyFont: {
                    size: 16, // Увеличил размер основного текста (Сделок, Объем...)
                    weight: 500,
                    family: 'Inter, sans-serif'
                },
                // Увеличиваем отступ между заголовком и текстом
                titleSpacing: 6,
                bodySpacing: 6,
                callbacks: {
                    title: (tooltipItems: TooltipItem<'line'>[]) => {
                        const index = tooltipItems[0].dataIndex;
                        if (tooltipLabels && tooltipLabels[index]) {
                            return tooltipLabels[index];
                        }
                        return tooltipItems[0].label;
                    },
                    label: (tooltipItem: TooltipItem<'line'>) => {
                        if (renderTooltip) {
                            const val = tooltipItem.parsed.y || 0;
                            return renderTooltip(tooltipItem.dataIndex, val);
                        }
                        return `${tooltipItem.formattedValue}`;
                    }
                }
            }
        },
        scales: {
            x: {
                display: true,
                grid: { display: false },
                ticks: {
                    color: '#858d9d',
                    font: { size: 10 },
                    maxRotation: 0,
                    autoSkip: false,
                }
            },
            y: {
                display: true,
                beginAtZero: true,
                grid: {
                    color: '#2c2d30',
                    borderDash: [5, 5],
                },
                ticks: {
                    color: '#858d9d',
                    font: { size: 10 },
                    maxTicksLimit: 5,
                    padding: 10,
                    callback: (value) => formatCompact(Number(value))
                },
            },
        },
        elements: {
            point: {
                radius: 0,
                hoverRadius: 6,
                hoverBackgroundColor: colorHex,
                hoverBorderColor: '#fff',
                hoverBorderWidth: 2
            },
            line: { tension: 0.2 },
        },
        layout: { padding: { top: 10 } }
    }), [colorHex, renderTooltip, tooltipLabels]);

    const chartData: ChartData<'line'> = useMemo(() => {
        const normalizedData = data.length === 1 ? [data[0], data[0]] : data;
        const normalizedLabels = labels.length === 1 ? [labels[0], labels[0]] : labels;

        const rgb = hexToRgb(colorHex);

        return {
            labels: normalizedLabels,
            datasets: [
                {
                    data: normalizedData,
                    borderColor: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
                    borderWidth: 2,
                    fill: true,
                    backgroundColor: (context: ScriptableContext<'line'>) => {
                        const ctx = context.chart.ctx;
                        const gradient = ctx.createLinearGradient(0, 0, 0, context.chart.height);
                        gradient.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.45)`);
                        gradient.addColorStop(0.5, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`);
                        gradient.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.0)`);
                        return gradient;
                    },
                },
            ],
        };
    }, [data, labels, colorHex]);

    return (
        <div style={{ position: 'relative', width: '100%', height: '250px', zIndex: 5, minWidth: 0 }}>
            <Line ref={chartRef} data={chartData} options={chartOptions} plugins={[shadowLine]} />
        </div>
    );
};

export default MiniChart;